import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { supabase } from "@/integrations/supabase/client";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

interface ProductFAQItem {
  id: string;
  question: string;
  answer: string;
  display_order: number;
  category: string;
}

const CATEGORY_LABELS: Record<string, string> = {
  general: "שאלות נפוצות",
  shipping: "משלוח",
};

export const ProductFAQ = () => {
  const [faqs, setFaqs] = useState<ProductFAQItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadFAQs = async () => {
      try {
        const { data, error } = await supabase
          .from("product_faq")
          .select("*")
          .eq("is_active", true)
          .order("display_order", { ascending: true });

        if (error) throw error;
        setFaqs(data || []);
      } catch (error) {
        console.error("Error loading product FAQs:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadFAQs();
  }, []);

  if (isLoading || faqs.length === 0) {
    return null;
  }

  // Group FAQs by category
  const faqsByCategory = faqs.reduce((acc, faq) => {
    const category = faq.category || 'general';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(faq);
    return acc;
  }, {} as Record<string, ProductFAQItem[]>);

  // Order categories: general first, then shipping
  const orderedCategories = ['general', 'shipping'].filter(cat => faqsByCategory[cat]?.length > 0);

  // Build FAQ schema for AI Overviews / rich results
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <section className="mt-16 animate-fade-in" style={{ animationDelay: '0.25s', animationFillMode: 'backwards' }}>
      {/* FAQ JSON-LD for product pages */}
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      <div className="text-center mb-8">
        <h2 className="text-2xl mb-3" style={{ fontWeight: 300 }}>שאלות נפוצות</h2>
        <div className="w-12 h-px bg-primary/40 mx-auto"></div>
      </div>
      
      <div className="space-y-8">
        {orderedCategories.map((category) => (
          <div key={category}>
            <h3 className="text-lg mb-4 text-muted-foreground" style={{ fontWeight: 400 }}>
              {CATEGORY_LABELS[category] || category}
            </h3>
            <Accordion type="single" collapsible className="w-full">
              {faqsByCategory[category].map((faq) => (
                <AccordionItem key={faq.id} value={faq.id} className="border-b border-border">
                  <AccordionTrigger className="text-right hover:no-underline py-4" style={{ fontWeight: 400 }}>
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground pb-4 whitespace-pre-wrap" style={{ fontWeight: 300 }}>
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        ))}
      </div>
    </section>
  );
};

export default ProductFAQ;
